/* eslint-disable consistent-return */
/* eslint-disable max-nested-callbacks */
import { NITRATES_CODE, DEFAULT_OBJECTIVE } from 'catchment/constants/CatchmentConstant'
import QualityChart from 'components/echart/QualityChart'
import { flatten, groupBy, isNil, isUndefined, max, maxBy, minBy, orderBy, uniqBy, compact, keys, round, uniq, template, isEmpty, min } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import i18n from 'simple-react-i18n'
import { calculateAverage, REMARK_CODES, searchMaxAnalysis, searchMinAnalysis, searchP90Analysis } from 'utils/AnalyseUtils'
import { getBeginingOfTheYear, getDate, getYear } from 'utils/DateUtil'
import { hasValue } from 'utils/NumberUtil'
import TooltipAnalyse from 'quality/components/qualityComponents/TooltipAnalyse'
import { formatHypeTrend, formatHypeTrendRupture } from 'utils/HypeUtils'
import IAEauAction from 'iaeau/IAEauAction'
import { HYPE_TRENDS_CONSTANTS } from 'quality/constants/QualityConstants'
import ProgressCard from 'components/card/ProgressCard'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import MessageCard from 'components/card/MessageCard'
import { exportFile } from 'utils/ExportDataUtil'
import AnalysisAction from 'quality/actions/AnalysisAction'
import DtoAnalysisLight from 'quality/dto/analyse/DtoAnalysisLight'
import { Card, CardContent, Grid, IconButton, Icon, Tooltip, Button, Dialog, DialogActions, DialogContent, Stack } from '@mui/material'
import { CardTitle } from 'components/card/NewCard'
import { ALIGN, CardTable, NewTable } from 'components/datatable/NewTable'
import { calculateThresholdsResult, compareToThreshold, THRESHOLD_COLOR, THRESHOLD_DISCRIMINANT, THRESHOLD_DISPLAY, THRESHOLD_TYPE } from 'utils/QualityThresholdUtils'
import SliderPanel from 'components/slider/SliderPanel'
import { CheckBox, CheckBoxOutlineBlank, HelpRounded, Visibility, VisibilityOff } from '@mui/icons-material'
import { blackColor, THRESHOLD_COLORS_CODES } from 'utils/constants/ColorTheme'
import { getSettingBool } from 'utils/SettingUtils'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import useBoolean from 'utils/customHook/useBoolean'
import { filterIcon } from 'components/echart/EChartUtils'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { SHOW_SANDRE_CODE, SHOW_INSEE_CODE } from 'account/constants/AccountSettingsConstants'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import ObjectiveChart from 'catchment/components/catchment/dashboard/CatchmentObjectives'
import DtoQualitometerLight from 'quality/dto/DtoQualitometerLight'
import { NitratesChartAnnualEvolution } from 'catchment/components/catchment/dashboard/CatchmentAnnualEvolution'

const getThresholdColor = (value, threshold) => {
    if (isNil(threshold) || isNil(value)) return
    return compareToThreshold(value, threshold, THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL) ? THRESHOLD_COLOR.red : THRESHOLD_COLOR.green
}

const getTooltip = (analyse, threshold, showCode = true) => {
    if (!analyse || !threshold) return {}
    return {
        tooltip: (
            <TooltipAnalyse
                title={`${i18n.nitrates} ${showCode ? `[${NITRATES_CODE}]` : ''}`}
                analyse={analyse}
                threshold={{
                    parameterCode: analyse.parameter,
                    threshold1: threshold,
                    thresholdType: THRESHOLD_TYPE.INCREASING,
                    display: THRESHOLD_DISPLAY.INCREASING,
                    discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
                }}
                overrideColors={[THRESHOLD_COLOR.green, THRESHOLD_COLOR.red]}
            />
        ),
    }
}

const getAverageTooltip = (result, threshold, showCode = true) => {
    if (!result || !threshold) return {}
    return {
        tooltip: (
            <SliderPanel
                title={`${i18n.nitrates} ${showCode ? `[${NITRATES_CODE}]` : ''}`}
                analysis={{ result }}
                threshold={{
                    parameterCode: NITRATES_CODE,
                    threshold1: threshold,
                    thresholdType: THRESHOLD_TYPE.INCREASING,
                    display: THRESHOLD_DISPLAY.INCREASING,
                    discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
                }}
                withThresholdLevels
                overrideColors={[THRESHOLD_COLOR.green, THRESHOLD_COLOR.red]}
            />
        ),
    }
}

const FollowUpNitratesTable = ({
    nitratesAnalysis = [],
    threshold,
}) => {
    const {
        qualitometers,
        cities,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        cities: store.CityReducer.cities,
    }), shallowEqual)

    const nitratesAnalysisGroup = useMemo(() => groupBy(nitratesAnalysis, STATION_TYPE_NAME.qualitometer), [nitratesAnalysis])

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const analysisFormated = useMemo(() => {
        const formated = keys(nitratesAnalysisGroup).map(key => {
            const qualitometer = qualitometers.find(q => q.id === parseInt(key))
            const city = qualitometer && cities.find(c => c.code === qualitometer.townCode)
            const stationName = [qualitometer?.code ? `[${qualitometer.code}]` : '', `${qualitometer?.name || ''}`, `${city?.name ? `- ${city.name}` : ''}`].join(' ')
            const yearGrounp = groupBy(nitratesAnalysisGroup[key], a => getYear(a.sampleDate))
            return keys(yearGrounp).map(year => {
                const analysis = yearGrounp[year].map(a => ({ ...a, ...calculateThresholdsResult(a, []) }))

                const p90 = searchP90Analysis(analysis)
                const minValue = searchMinAnalysis(analysis)
                const maxValue = searchMaxAnalysis(analysis)
                const average = calculateAverage(analysis)

                return {
                    catchmentPoint: { value: stationName },
                    year: { value: year },
                    percentile90: {
                        value: p90?.value,
                        align: ALIGN.RIGHT,
                        color: getThresholdColor(p90?.result, threshold),
                        ...getTooltip(p90, threshold, showSandreCode),
                    },
                    min: {
                        value: minValue?.value,
                        align: ALIGN.RIGHT,
                        color: getThresholdColor(minValue?.result, threshold),
                        ...getTooltip(minValue, threshold, showSandreCode),
                    },
                    average: {
                        value: average,
                        align: ALIGN.RIGHT,
                        color: getThresholdColor(average, threshold),
                        ...getAverageTooltip(average, threshold, showSandreCode),
                    },
                    max: {
                        value: maxValue?.value,
                        align: ALIGN.RIGHT,
                        color: getThresholdColor(maxValue?.result, threshold),
                        ...getTooltip(maxValue, threshold, showSandreCode),
                    },
                    nbQuantifications: { value: analysis.length, align: ALIGN.RIGHT },
                }
            })
        })
        return orderBy(flatten(formated), ['catchmentPoint.value', 'year.value'], ['asc', 'desc'])
    }, [cities, nitratesAnalysisGroup, qualitometers, threshold, showSandreCode])

    const exportAction = useMemo(() =>([{
        icon: 'file_download',
        tooltip: i18n.export,
        onClick: () => {
            const dataFormated = keys(nitratesAnalysisGroup).map(key => {
                const qualitometer = qualitometers.find(q => q.id === parseInt(key))
                const city = qualitometer && cities.find(c => c.code === qualitometer.townCode)
                const yearGrounp = groupBy(nitratesAnalysisGroup[key], a => getYear(a.sampleDate))
                return keys(yearGrounp).map(year => {
                    const analysis = yearGrounp[year].map(a => ({ ...a, ...calculateThresholdsResult(a, []) }))

                    const p90 = searchP90Analysis(analysis)
                    const analysisMin = searchMinAnalysis(analysis)
                    const maxValue = searchMaxAnalysis(analysis)
                    const average = calculateAverage(analysis)

                    return {
                        code: { value: qualitometer?.code, cellType: 'string' },
                        name: qualitometer?.name,
                        cityCode: city?.code,
                        cityLabel: city?.name,
                        year: { value: year, format: 'yyyy', cellType: 'date' },
                        percentile90: { value: p90?.value, cellType: 'right' },
                        min: { value: analysisMin?.value, cellType: 'right' },
                        average: { value: average, cellType: 'number' },
                        max: { value: maxValue?.value, cellType: 'right' },
                        nbQuantifications: { value: analysis.length, format: '0', cellType: 'number' },
                    }
                })
            })
            const dataOrdered = orderBy(flatten(dataFormated), ['code.value', 'year.value'], ['asc', 'desc'])
            const data = dataOrdered.length ? [{ ...dataOrdered[0], headers: keys(dataOrdered[0]) }, ...dataOrdered.slice(1)] : []
            exportFile({
                data,
                exportType: 'xlsx',
                titleFile: i18n.nitratesFollowUp,
            })
        },
    }]), [nitratesAnalysisGroup, qualitometers, cities])

    return (
        <CardTable
            title={i18n.nitratesConcentrationStatistics}
            rows={analysisFormated}
            headers={[
                { key: 'catchmentPoint', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.catchmentPoint}</span>) },
                'year',
                'percentile90',
                'min',
                'average',
                'max',
                { key: 'nbQuantifications', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.nbQuantifications}</span>) },
            ]}
            actions={exportAction}
            rowsPerPageOptions={nbPerPageLabel}
            hideNbElements
            centerTitle
        />
    )
}

FollowUpNitratesTable.propTypes = {
    nitratesAnalysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight)),
    threshold: PropTypes.number,
}

const EXPORT_ALL_HEADERS = ['code', 'name', 'cityCode', 'cityLabel', 'exportMax', 'exportPercentile90', 'trend', 'sageThreshold', 'exceedingFrequency', 'exceedingNb', 'exceedingYears']
const EXPORT_NO_INSEE_CODE = ['code', 'name', 'cityLabel', 'exportMax', 'exportPercentile90', 'trend', 'sageThreshold', 'exceedingFrequency', 'exceedingNb', 'exceedingYears']

const StatisticsNitratesTable = ({
    nitratesAnalysis = [],
    threshold,
    endPeriod,
}) => {
    const {
        catchment,
        qualitometers,
        cities,
    } = useSelector(store => ({
        catchment: store.CatchmentReducer.catchment,
        qualitometers: store.QualityReducer.qualitometersLight,
        cities: store.CityReducer.cities,
    }), shallowEqual)

    const tenYearsAgo = getBeginingOfTheYear(getYear(endPeriod) - 10 + 1)
    const fiveYearsAgo = getBeginingOfTheYear(getYear(endPeriod) - 5 + 1)
    const threeYearsAgo = getBeginingOfTheYear(getYear(endPeriod) - 3 + 1)

    const formattedAnalysis = nitratesAnalysis.filter(a => a.sampleDate >= tenYearsAgo && a.remark === REMARK_CODES.VALID).map(a => ({ ...a, ...calculateThresholdsResult(a, []) }))

    const fiveYearsAnalysisGroup = useMemo(() => groupBy(formattedAnalysis.filter(a => a.sampleDate > fiveYearsAgo), STATION_TYPE_NAME.qualitometer), [formattedAnalysis, fiveYearsAgo])
    const threeYearsAnalysisGroup = useMemo(() => groupBy(formattedAnalysis.filter(a => a.sampleDate > threeYearsAgo), STATION_TYPE_NAME.qualitometer), [formattedAnalysis, threeYearsAgo])
    const referenceAnalysisGroup = useMemo(() => groupBy(formattedAnalysis.filter(a => fiveYearsAgo > a.sampleDate), STATION_TYPE_NAME.qualitometer), [formattedAnalysis, fiveYearsAgo])

    const sageThreshold = catchment?.sageThresholdNitrates ?? threshold

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)
    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const analysisFormated = useMemo(() => {
        const formated = keys(fiveYearsAnalysisGroup).map(key => {
            const qualitometer = qualitometers.find(q => q.id === parseInt(key))
            const city = qualitometer && cities.find(c => c.code === qualitometer.townCode)
            const stationName = [qualitometer?.code ? `[${qualitometer.code}]` : '', `${qualitometer?.name || ''}`, `${city?.name ? `- ${city.name}` : ''}`].join(' ')
            const maxValue = searchMaxAnalysis(threeYearsAnalysisGroup[key])
            const p90 = searchP90Analysis(threeYearsAnalysisGroup[key])?.result

            const referenceAverage = calculateAverage(referenceAnalysisGroup[key])
            const fiveLastYearsAverage = calculateAverage(fiveYearsAnalysisGroup[key])
            const evolutionPercent = referenceAverage ? round(fiveLastYearsAverage / referenceAverage * 100 -100, 2) : undefined
            const trend = evolutionPercent ? evolutionPercent >= 5 ? i18n.rise : evolutionPercent <= -5 ? i18n.decrease : i18n.stable : i18n.noDataEnough

            const exceedingAnalysis = fiveYearsAnalysisGroup[key].filter(a => a.result > sageThreshold)
            const nbExceedingAnalysis = exceedingAnalysis.length
            const totalAnalysis = fiveYearsAnalysisGroup[key].length
            const exceedingYears = uniq(exceedingAnalysis.map(a => getYear(a.sampleDate))).sort((a, b) => a - b).reduce((str, year) => `${str} ${year?.toString()}`, '')
            const exceedingFrequency = totalAnalysis > 0 ? round(nbExceedingAnalysis / totalAnalysis * 100, 2) : 0

            return {
                code: { value: qualitometer?.code, cellType: 'string' },
                name: qualitometer?.name,
                cityCode: city?.code,
                cityLabel: city?.name,
                catchmentPoint: { value: stationName },
                max: {
                    value: maxValue?.value ? round(maxValue?.value, 4) : undefined,
                    align: ALIGN.RIGHT,
                    color: getThresholdColor(maxValue?.result, threshold),
                    ...getTooltip(maxValue, threshold, showSandreCode),
                },
                percentile90: {
                    value: p90 ? round(p90, 4) : undefined,
                    align: ALIGN.RIGHT,
                    color: getThresholdColor(p90, threshold),
                    ...getAverageTooltip(p90, threshold, showSandreCode),
                },
                exportMax: {
                    value: maxValue?.value ? round(maxValue?.value, 4) : undefined,
                    cellType: 'number',
                },
                exportPercentile90: {
                    value: p90 ? round(p90, 4) : undefined,
                    align: ALIGN.RIGHT,
                    cellType: 'number',
                },
                trend,
                sageThreshold: { value: sageThreshold, align: ALIGN.RIGHT, cellType: 'number' },
                exceedingFrequency: { value: `${exceedingFrequency} %`, align: ALIGN.RIGHT, cellType: 'right' },
                exceedingNb: { value: nbExceedingAnalysis, align: ALIGN.RIGHT, cellType: 'number', format: '0' },
                exceedingYears: { value: exceedingYears, align: ALIGN.RIGHT, cellType: 'string' },
            }
        })
        return orderBy(flatten(formated), ['catchmentPoint.value'], ['asc'])
    }, [cities, referenceAnalysisGroup, fiveYearsAnalysisGroup, threeYearsAnalysisGroup, qualitometers, threshold, sageThreshold, showSandreCode])


    const exportAction = useMemo(() =>([{
        icon: 'file_download',
        tooltip: i18n.export,
        onClick: () => {
            const dataOrdered = orderBy(flatten(analysisFormated), ['code.value'], ['asc'])
            const data = [{
                ...dataOrdered[0],
                headers: showInseeCode ? EXPORT_ALL_HEADERS : EXPORT_NO_INSEE_CODE,
                customHeaders: compact(['code', 'name', showInseeCode ? 'cityCode' : '', 'cityLabel', 'threeLastYearsMax', 'threeLastYearsAveragePercentile90', 'fiveLastYearsTrend', `${i18n.sageObjective} ${i18n.nitrates}`, 'overrunTx', 'overrunNb', 'overrunYears']),
            },
            ...dataOrdered.slice(1),
            ]
            exportFile({
                data,
                exportType: 'xlsx',
                titleFile: i18n.fiveLastYearsStatistics,
            })
        },
    }]), [analysisFormated, showInseeCode])

    return (
        <CardTable
            title={i18n.fiveLastYearsStatistics}
            rows={analysisFormated}
            headers={[
                { key: 'catchmentPoint', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.catchmentPoint}</span>) },
                { key: 'max', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.threeLastYearsMax}</span>) },
                { key: 'percentile90', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.threeLastYearsAveragePercentile90}</span>) },
                { key: 'trend', value: (
                    <Tooltip title={i18n.fiveLastYearsTrendExplanation}>
                        <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.fiveLastYearsTrend}</span>
                    </Tooltip>
                ) },
                { key: 'sageThreshold', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.sageObjective} ${i18n.nitrates}`}</span>) },
                { key: 'exceedingFrequency', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.overrunTx}</span>) },
                { key: 'exceedingNb', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.overrunNb}</span>) },
                { key: 'exceedingYears', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.overrunYears}</span>) },
            ]}
            actions={exportAction}
            rowsPerPageOptions={nbPerPageLabel}
            hideNbElements
            centerTitle
        />
    )
}

StatisticsNitratesTable.propTypes = {
    nitratesAnalysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight)),
    threshold: PropTypes.number,
    endPeriod: PropTypes.number,
}

const FollowUpNitratesChart = ({
    nitratesAnalysis = [],
    qualitometersFiltered = [],
    stations,
    selectedTrend,
    threshold,
    trendsMannKendall = [],
    trendsRupture = [],
    startPeriod,
    endPeriod,
    openPopin = () => {},
}) => {
    const {
        qualitometers,
        cities,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        cities: store.CityReducer.cities,
    }), shallowEqual)

    const analysisFiltered = useMemo(() => {
        if (stations.length > 0) {
            return nitratesAnalysis.filter(({ qualitometer }) => stations.includes(qualitometer))
        }
        return nitratesAnalysis
    }, [nitratesAnalysis, stations])

    const nitratesAnalysisGroup = useMemo(() => groupBy(nitratesAnalysis, STATION_TYPE_NAME.qualitometer), [nitratesAnalysis])

    const analysisFormated = useMemo(() => keys(nitratesAnalysisGroup).map(key => {
        const filteredNitratesAnalysis = nitratesAnalysisGroup[key].filter(a => a.remark === REMARK_CODES.VALID)
        const { qualitometer: qualitometerId } = nitratesAnalysisGroup[key][0]
        const qualitometer = qualitometers.find(q => q.id === qualitometerId)
        const city = qualitometer && cities.find(c => c.code === qualitometer.townCode)
        return {
            idStation: qualitometerId,
            name: [qualitometer?.code ? `[${qualitometer.code}]` : '', `${qualitometer?.name || ''}`, `${city?.name ? `- ${city.name}` : ''}`].join(' '),
            unit: 'mg/L',
            dataList: filteredNitratesAnalysis.map(analyse => ({ date: analyse.sampleDate, value: analyse.result })),
        }
    }), [nitratesAnalysisGroup, qualitometers, cities])

    const analysisFormatedFiltered = useMemo(() => {
        if (stations.length > 0) {
            return analysisFormated.filter(({ idStation }) => stations.includes(idStation))
        }
        return analysisFormated
    }, [analysisFormated, stations])

    const hypeFormated = useMemo(() => {
        const minDate = minBy(analysisFiltered, 'sampleDate')?.sampleDate
        const maxDate = maxBy(analysisFiltered, 'sampleDate')?.sampleDate

        const qualitometer = qualitometers.find(q => q.id === selectedTrend)
        const trendMannKendall = qualitometer ? trendsMannKendall.find(t => qualitometer.code === t.stationCode) : undefined
        const trendRupture = qualitometer ? trendsRupture.find(t => qualitometer.code === t.stationCode) : undefined

        const mannKendallTrend = trendMannKendall ? [{
            name: i18n.mannKendallTrend,
            showSymbol: false,
            dataList: formatHypeTrend(trendMannKendall.coefficient, trendMannKendall.value, minDate, maxDate),
        }] : []

        if (trendRupture && (['preValue', 'preCoefficient', 'breakDate'].every(k => hasValue(trendRupture[k])) || ['postValue', 'postCoefficient', 'breakDate'].every(k => hasValue(trendRupture[k])))) {
            const maxValue = max([maxBy(analysisFiltered, 'result')?.result || 0, threshold])
            const ruptureLine = {
                name: i18n.trendRupture,
                dataList: [
                    { date: trendRupture.breakDate, value: 1.1 * maxValue },
                    { date: trendRupture.breakDate, value: 0 },
                ],
                lineStyle: { type: 'dashed' },
            }
            const rupture = {
                name: i18n.trendRupture,
                connectNulls: false,
                showSymbol: false,
                dataList: formatHypeTrendRupture(trendRupture, minDate, maxDate),
            }
            return [
                ...mannKendallTrend,
                ruptureLine,
                rupture,
            ]
        }

        return mannKendallTrend
    }, [analysisFiltered, threshold, selectedTrend, qualitometers, trendsMannKendall, trendsRupture])

    const myToolActions = qualitometersFiltered.length > 1 ? { title: i18n.filterResult, svg: filterIcon, action: openPopin } : undefined

    const series = [...analysisFormatedFiltered, ...hypeFormated]
    const allValues = series.flatMap(q => q.dataList?.map(v => v.value))
    const fixMaxValue = max(allValues) > threshold ? {} : { max: threshold }

    return (
        <QualityChart
            title={i18n.nitratesMonitoring}
            exportName={i18n.nitratesFollowUp}
            grids={[{ height: 300 }]}
            xAxis={[{ min: startPeriod, max: endPeriod }]}
            yAxis={[{ name: `${i18n.nitratesContent} (mg/L)`, min: min([...allValues, 0]), ...fixMaxValue }]}
            series={series}
            thresholds={[{ name: i18n.nitratesThreshold, dataList: [{ value: threshold }].filter(t => !isUndefined(t.value)) }]}

            headerHeight={0}

            withToolQuantification
            withToolTypeLine
            withToolTypeBar
            withToolLog
            withToolThreshold
            withToolMarker
            withToolLine
            withToolActions={compact([myToolActions])}
            withToolFullScreen
            withToolZoom='FOLLOW_UP_NITRATES_CHART'
        />
    )
}

FollowUpNitratesChart.propTypes = {
    startPeriod: PropTypes.number,
    endPeriod: PropTypes.number,
    nitratesAnalysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight)),
    qualitometersFiltered: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.number,
    })),
    threshold: PropTypes.number,
    stations: PropTypes.arrayOf(PropTypes.number),
    selectedTrend: PropTypes.number,
    trendsMannKendall: PropTypes.arrayOf(PropTypes.shape({})),
    trendsRupture: PropTypes.arrayOf(PropTypes.shape({})),
    openPopin: PropTypes.func,
}

const HypeTable = ({
    nitratesAnalysis = [],
    qualitometersFiltered = [],
    selectedTrend,
    setSelectedTrend = () => { },
    trendsMannKendall = [],
    trendsRupture = [],
}) => {
    const {
        qualitometers,
        cities,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        cities: store.CityReducer.cities,
    }), shallowEqual)

    const groupedAnalysis = useMemo(() => {
        const quantifiedAnalysis = nitratesAnalysis.filter(a => a.remark === REMARK_CODES.VALID)
        const groupByQualitometer = groupBy(quantifiedAnalysis, STATION_TYPE_NAME.qualitometer)
        return keys(groupByQualitometer).reduce((acc, id) => {
            const { [id]: analysis = [] } = groupByQualitometer
            acc[id] = analysis.map(a => ({
                result: a.result,
                sampleDate: a.sampleDate,
            }))
            return acc
        }, {})
    }, [nitratesAnalysis])

    const analysisFormated = useMemo(() => {
        return qualitometersFiltered.map(point => {
            const qualitometer = qualitometers.find(q => q.id === point.id)
            const { [point.id]: analysis = [] } = groupedAnalysis
            const trend = trendsMannKendall.find(t => t.stationCode === qualitometer.code)
            const rupture = trendsRupture.find(t => t.stationCode === qualitometer.code)
            const mannKendallTrend = (!hasValue(trend) || trend?.coefficient === 0) && i18n.noTrend || trend.coefficient > 0 && i18n.rise || i18n.decrease
            const city = qualitometer && cities.find(c => c.code === qualitometer.townCode)
            const stationName = [qualitometer?.code ? `[${qualitometer.code}]` : '', `${qualitometer?.name || ''}`, `${city?.name ? `- ${city.name}` : ''}`].join(' ')
            const icon = (
                <Tooltip title={point.id === selectedTrend ? i18n.hideTrend : i18n.displayTrend }>
                    <IconButton
                        sx={{ color: blackColor }}
                        size='actionTable'
                        onClick={ () => setSelectedTrend(point.id === selectedTrend ? undefined : point.id) }
                    >
                        <Icon fontSize='inherit'>{ point.id === selectedTrend ? <VisibilityOff /> : <Visibility /> }</Icon>
                    </IconButton>
                </Tooltip>
            )

            return {
                nullValueIconButton: { value: (mannKendallTrend === i18n.noTrend && isUndefined(rupture)) ? <></> : icon },
                catchmentPoint: { value: stationName },
                nbQuantifications: { value: analysis.length },
                mannKendallTrend: { value: mannKendallTrend },
                ruptureDate: { value: rupture?.breakDate ? getDate(rupture.breakDate) : i18n.noRupture },
                id: point.id,
            }
        })
    }, [groupedAnalysis, qualitometers, qualitometersFiltered, trendsMannKendall, trendsRupture, selectedTrend, cities])

    const exportAction = useMemo(() =>([{
        icon: 'file_download',
        tooltip: i18n.export,
        onClick: () => {
            const dataFormated = qualitometersFiltered.map(point => {
                const qualitometer = qualitometers.find(q => q.id === point.id)
                const { [point.id]: analysis = [] } = groupedAnalysis
                const trend = trendsMannKendall.find(t => t.stationCode === qualitometer.code)
                const rupture = trendsRupture.find(t => t.stationCode === qualitometer.code)
                const mannKendallTrend = (!hasValue(trend) || trend?.coefficient === 0) && i18n.noTrend || trend.coefficient > 0 && i18n.rise || i18n.decrease
                const city = qualitometer && cities.find(c => c.code === qualitometer.townCode)

                return {
                    code: { value: qualitometer?.code, cellType: 'string' },
                    name: qualitometer?.name,
                    cityCode: city?.code,
                    cityLabel: city?.name,
                    nbQuantifications: { value: analysis.length, cellType: 'right' },
                    mannKendallTrend: { value: mannKendallTrend, cellType: 'string' },
                    ruptureDate: { value: rupture?.breakDate ? getDate(rupture.breakDate) : i18n.noRupture, cellType: 'string' },
                }
            })
            const dataOrdered = orderBy(flatten(dataFormated), ['code.value'], ['asc'])
            const data = dataOrdered.length ? [{ ...dataOrdered[0], headers: keys(dataOrdered[0]) }, ...dataOrdered.slice(1)] : []
            exportFile({
                data,
                exportType: 'xlsx',
                titleFile: i18n.nitratesFollowUp,
            })
        },
    }]), [qualitometers, cities, qualitometersFiltered, trendsMannKendall, trendsRupture, groupedAnalysis])

    return (
        <CardTable
            title={i18n.nitratesTrendsConcentration}
            headers={[
                'nullValueIconButton',
                { key: 'catchmentPoint', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.catchmentPoint}</span>) },
                { key: 'nbQuantifications', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.nbQuantifications}</span>) },
                { key: 'mannKendallTrend', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.mannKendallTrend}</span>) },
                { key: 'ruptureDate', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.ruptureDate}</span>) },
            ]}
            rows={analysisFormated}
            actions={exportAction}
            hideNbElements
            centerTitle
        />
    )
}

HypeTable.propTypes = {
    nitratesAnalysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight)),
    qualitometersFiltered: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
    })),
    selectedTrend: PropTypes.number,
    setSelectedTrend: PropTypes.func,
    trendsMannKendall: PropTypes.arrayOf(PropTypes.shape({})),
    trendsRupture: PropTypes.arrayOf(PropTypes.shape({})),
}

const HypeCardNitrates = ({
    nitratesAnalysis = [],
    qualitometersFiltered = [],
    threshold,
    startPeriod,
    endPeriod,
}) => {
    const dispatch = useDispatch()

    const {
        qualitometers,
        cities,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        cities: store.CityReducer.cities,
    }), shallowEqual)

    const [trendsMannKendall, setTrendsMannKendall] = useState([])
    const [trendsRupture, setTrendsRupture] = useState([])
    const [selectedTrend, setSelectedTrend] = useState(null)
    const [stations, setStations] = useState([])

    const {
        value: isPopinOpen,
        setTrue: openPopin,
        setFalse: closePopin,
    } = useBoolean(false)

    const groupedAnalysis = useMemo(() => {
        const quantifiedAnalysis = nitratesAnalysis.filter(a => a.remark === REMARK_CODES.VALID)
        return groupBy(quantifiedAnalysis, STATION_TYPE_NAME.qualitometer)
    }, [nitratesAnalysis])

    useEffect(() => {
        if (!nitratesAnalysis.length) {
            return
        }
        qualitometersFiltered.forEach(({ id }) => {
            const qualitometer = qualitometers.find(q => q.id === id)
            if (!qualitometer) {
                return
            }
            const { [id]: analysis = [] } = groupedAnalysis

            const hypeData = analysis.map((a, index) => ({
                stationCode: qualitometer.code,
                stationTypeCode: qualitometer.stationType,
                parameterCode: parseInt(a.parameter) || 0,
                analysisNumber: index,
                measureDate: a.sampleDate,
                measure: a.result,
                unitCode: 0,
                remarkCode: 1,
            }))
            if (hypeData.length) {
                dispatch(IAEauAction.getHypeTrend(hypeData, qualitometer.code)).then(({ trends, breaks }) => {
                    const trendMannKendall = trends.filter(t => t.statisticsType === HYPE_TRENDS_CONSTANTS.MANN_KENDALL)
                    const trendRupture = breaks.filter(t => t.statisticsType === HYPE_TRENDS_CONSTANTS.TREND_RUPTURE)
                    setTrendsMannKendall(p => uniqBy([...trendMannKendall, ...p], 'stationCode'))
                    setTrendsRupture(p => uniqBy([...trendRupture, ...p], 'stationCode'))
                })
            }
        })
    }, [dispatch, groupedAnalysis, nitratesAnalysis.length, qualitometers, qualitometersFiltered])

    const pointsFormated = useMemo(() => {
        return qualitometersFiltered.map(point => {
            const qualitometer = qualitometers.find(q => q.id === point.id)
            const { [point.id]: analysis = [] } = groupedAnalysis
            const city = qualitometer && cities.find(c => c.code === qualitometer.townCode)
            const stationName = [qualitometer.code ? `[${qualitometer.code}]` : '', `${qualitometer.name || ''}`, `${city?.name ? `- ${city.name}` : ''}`].join(' ')
            return {
                catchmentPoint: { value: stationName },
                nbQuantifications: { value: analysis.length },
                id: point.id,
            }
        })
    }, [groupedAnalysis, qualitometers, qualitometersFiltered, cities])
    return (
        <>
            <FollowUpNitratesChart
                startPeriod={startPeriod}
                endPeriod={endPeriod}
                nitratesAnalysis={nitratesAnalysis}
                qualitometersFiltered={qualitometersFiltered}
                threshold={threshold}
                stations={stations}
                selectedTrend={selectedTrend}
                trendsMannKendall={trendsMannKendall}
                trendsRupture={trendsRupture}
                openPopin={openPopin}
            />
            <div style={{ margin: '0px 10vw 0px 10vw' }}>
                <HypeTable
                    nitratesAnalysis={nitratesAnalysis}
                    qualitometersFiltered={qualitometersFiltered}
                    selectedTrend={selectedTrend}
                    setSelectedTrend={setSelectedTrend}
                    trendsMannKendall={trendsMannKendall}
                    trendsRupture={trendsRupture}
                />
            </div>
            <Dialog
                maxWidth='lg'
                open={isPopinOpen}
            >
                <DefaultDialogTitle
                    title={
                        <Grid container columnSpacing={1}>
                            {i18n.selectSamplingPointsToAdd}
                            <Tooltip title={i18n.selectNoneOrAll}>
                                <HelpRounded />
                            </Tooltip>
                        </Grid>
                    }
                    onClose={closePopin}
                />
                <DialogContent>
                    <Grid container rowSpacing={0.5} columnSpacing={1}>
                        <Grid size={12}>
                            <NewTable
                                headers={[
                                    { key: 'catchmentPoint', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.catchmentPoint}</span>) },
                                    { key: 'nbQuantifications', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.nbQuantifications}</span>) },
                                ]}
                                rows={pointsFormated}
                                lineActions={[{
                                    icon: ({ id }) => stations.includes(id) ? <CheckBox /> : <CheckBoxOutlineBlank />,
                                    onClick: ({ id }) => setStations(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]),
                                }]}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStations([])} variant='outlined'>
                        {i18n.reinit}
                    </Button>
                    <Button onClick={closePopin} variant='contained'>
                        {i18n.validate}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

HypeCardNitrates.propTypes = {
    startPeriod: PropTypes.number,
    endPeriod: PropTypes.number,
    nitratesAnalysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight)),
    qualitometersFiltered: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
    })),
    threshold: PropTypes.number,
}

const FormatObjectiveChart = ({
    qualitometers = [],
    analysis = [],
    threshold,
    startPeriod,
    endPeriod,
}) => {
    const {
        catchment,
    } = useSelector(store => ({
        catchment: store.CatchmentReducer.catchment,
    }), shallowEqual)

    const globalObjectiveNitrates = useApplicationSetting('NITRATES_OBJECTIVE', setting => setting ? parseFloat(setting) : DEFAULT_OBJECTIVE)
    const objectiveNitrates = useMemo(() => catchment.objectiveNitrates ?? globalObjectiveNitrates, [catchment.objectiveNitrates, globalObjectiveNitrates])

    const qualitometersLinked = useMemo(() => (
        catchment.catchmentPoints?.map(point => qualitometers.find(q => q.code === point.codeWithoutDesignation)).filter(q => !!q) || []
    ), [catchment.catchmentPoints, qualitometers])

    const qualitoCodes = useMemo(() => qualitometersLinked.map(q => q.code), [qualitometersLinked])
    const qualitoStations = useMemo(() => catchment.catchmentPoints?.filter(p => qualitoCodes?.includes(p.codeWithoutDesignation)), [catchment.catchmentPoints, qualitoCodes])
    const catchmentAndQualitoReducted = useMemo(() => ({
        id: catchment.id,
        name: catchment.name,
        code: catchment.code,
        points: qualitoStations?.map(p => ({
            codeWithoutDesignation: p.codeWithoutDesignation,
            mainPoint: p.mainPoint,
            qualito: qualitometers.filter(q => q.code === p.codeWithoutDesignation)?.map(q => ({ id: q.id, townCode: q.townCode, name: q.name })),
        })),
        qualito: catchment.catchmentPoints?.flatMap(p => qualitometers.filter(q => q.code === p.codeWithoutDesignation)).map(q => q.id),
    }), [catchment, qualitometers, qualitoStations])

    return (
        <ObjectiveChart
            analysis={analysis}
            objective={objectiveNitrates}
            threshold={threshold}
            catchmentAndQualito={catchmentAndQualitoReducted}
            fileName={i18n.annualNitratesObjective}
            title={template(i18n.annualNitratesObjectiveDetails)({ objective: objectiveNitrates, threshold })}
            startPeriod={startPeriod}
            endPeriod={endPeriod}
        />
    )
}

FormatObjectiveChart.propTypes = {
    qualitometers: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualitometerLight)),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight)),
    threshold: PropTypes.number,
    startPeriod: PropTypes.number,
    endPeriod: PropTypes.number,
}

const CardFollowUpNitrates = ({
    stationIds,
    startPeriod,
    endPeriod,
    producerExcluded,
    nitratesThreshold,
}) => {
    const {
        qualitometers,
        settings,
        cities,
        catchment,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        settings: store.AdministrationReducer.applicationSettings,
        cities: store.CityReducer.cities,
        catchment: store.CatchmentReducer.catchment,
    }), shallowEqual)

    const [analysis, setAnalysis] = useState([])

    const qualitometersFiltered = useMemo(() => {
        const points = stationIds?.map(id => {
            return qualitometers.find(q => q.id === id)
        }).filter(q => !!q) || []
        return points.map(({ code, townCode, name = '', id }) => {
            const city = cities.find(c => c.code === townCode)
            return {
                name: `[${code}] ${name} ${city?.name ? `- ${city.name}` : ''} `,
                id,
            }
        })
    }, [qualitometers, stationIds, cities])

    const { isLoaded } = useProgressDispatch(cancelRef => [
        AnalysisAction.getAnalysis({
            lightMode: true,
            stations: stationIds,
            startDate: startPeriod,
            endDate: endPeriod,
            excludedProducers: producerExcluded,
            parameters: [NITRATES_CODE],
            support: '3', // support 'eau'
        }).then(json => !cancelRef.current && setAnalysis(json?.map(j => new DtoAnalysisLight(j)))),
    ], [stationIds])

    const nitratesAnalysis = useMemo(() => {
        return analysis.filter(({ sampleDate, result }) => hasValue(sampleDate) && hasValue(result))
    }, [analysis])

    const nitratesAnalysisMoreFilters = useMemo(() => {
        return analysis.filter(({ qualitometer, remark }) => (
            qualitometers.some(q => q.id === qualitometer) &&
            remark === REMARK_CODES.VALID
        ))
    }, [analysis, qualitometers])

    const nitratesAnalysisGroup = useMemo(() => groupBy(nitratesAnalysisMoreFilters, STATION_TYPE_NAME.qualitometer), [nitratesAnalysisMoreFilters])

    const firstCatchmentYear = getSettingBool(settings, 'FIRST_CATCHMENT_YEAR')
    const newStartPeriod = (nitratesAnalysis.length && firstCatchmentYear) ? getBeginingOfTheYear(getDate(minBy(nitratesAnalysis, 'sampleDate').sampleDate, 'YYYY')) : startPeriod

    return (
        <Card data-cy='follow_up_nitrates'>
            <CardTitle title={i18n.nitratesFollowUp} />
            <CardContent>
                {!isLoaded && (
                    <ProgressCard indeterminate />
                )}
                {isLoaded && (
                    <>
                        {nitratesAnalysis.length ? (
                            <Stack spacing={2}>
                                <HypeCardNitrates
                                    startPeriod={newStartPeriod}
                                    endPeriod={endPeriod}
                                    nitratesAnalysis={nitratesAnalysis}
                                    threshold={nitratesThreshold}
                                    qualitometersFiltered={qualitometersFiltered}
                                />
                                <Stack spacing={2} sx={{ mx: '10vw' }}>
                                    <FollowUpNitratesTable nitratesAnalysis={nitratesAnalysis} threshold={nitratesThreshold} />
                                    <StatisticsNitratesTable
                                        nitratesAnalysis={nitratesAnalysis}
                                        threshold={nitratesThreshold}
                                        endPeriod={endPeriod}
                                    />
                                </Stack>
                                <Grid container sx={{ mt: '8px' }}>
                                    <Grid size={6} offset={(catchment && !isEmpty(catchment)) ? 0 : 3}>
                                        <NitratesChartAnnualEvolution
                                            analysis={nitratesAnalysisGroup}
                                            thresholds={[{
                                                dataList: [{ value: nitratesThreshold }],
                                                colorList: [THRESHOLD_COLORS_CODES.GREEN, THRESHOLD_COLORS_CODES.RED],
                                            }]}
                                            name={`${i18n.average} ${i18n.nitrates}`}
                                            unit='mg/L'
                                            startPeriod={newStartPeriod}
                                            endPeriod={endPeriod}
                                        />
                                    </Grid>
                                    { (catchment && !isEmpty(catchment)) && (
                                        <Grid size={6}>
                                            <FormatObjectiveChart
                                                qualitometers={qualitometers}
                                                analysis={nitratesAnalysisMoreFilters}
                                                threshold={nitratesThreshold}
                                                startPeriod={newStartPeriod}
                                                endPeriod={endPeriod}
                                            />
                                        </Grid>
                                    ) }
                                </Grid>
                            </Stack>
                        ) : (
                            <MessageCard>{i18n.noDataToDisplay}</MessageCard>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    )
}

CardFollowUpNitrates.propTypes = {
    stationIds: PropTypes.arrayOf(PropTypes.number),
    startPeriod: PropTypes.number,
    endPeriod: PropTypes.number,
    producerExcluded: PropTypes.arrayOf(PropTypes.number),
    nitratesThreshold: PropTypes.number,
}

export {
    FollowUpNitratesTable,
    FollowUpNitratesChart,
    HypeTable,
    HypeCardNitrates,
    CardFollowUpNitrates,
}